import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellRing, Filter, Search, ArrowRightCircle, CheckCircle2, ClipboardPlus, Ban } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FormField } from "@/components/common/FormSection";
import { api, formatApiError } from "@/lib/api";
import { timeAgo } from "@/pages/rpm/constants";

export default function RpmAlerts() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("__open__");
  const [severity, setSeverity] = useState("__all__");
  const [search, setSearch] = useState("");
  const [action, setAction] = useState(null);
  const [patientsMap, setPatientsMap] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status === "__open__") {
        // No backend OR; just fetch all and filter client-side
      } else if (status !== "__all__") {
        params.status = status;
      }
      if (severity !== "__all__") params.severity = severity;
      const r = await api.get("/rpm/alerts", { params: { ...params, per_page: 200 } });
      const rows = r.data.data ?? [];
      setItems(rows);
      // map patient ids → name
      const pids = Array.from(new Set(rows.map((a) => a.patient_id)));
      if (pids.length) {
        const map = {};
        await Promise.all(pids.map(async (pid) => {
          try {
            const p = await api.get(`/patients/${pid}`);
            const pt = p.data.data ?? {};
            map[pid] = `${pt.first_name} ${pt.last_name} (${pt.mrn})`;
          } catch {}
        }));
        setPatientsMap(map);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [status, severity]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => items.filter((a) => {
    if (status === "__open__" && ["resolved", "closed", "dismissed"].includes(a.status)) return false;
    if (search) {
      const hay = `${a.title} ${a.description} ${patientsMap[a.patient_id] || ""}`.toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  }), [items, status, search, patientsMap]);

  async function doAction() {
    if (!action) return;
    try {
      await api.post(`/rpm/alerts/${action.id}/${action.action}`, { reason: action.reason, notes: action.notes });
      toast.success(`Alert ${action.action}`);
      setAction(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div data-testid="rpm-alerts-page">
      <PageHeader title="RPM Active Alerts" subtitle="Threshold breaches, NEWS2, AI deterioration, missing-reading and device alerts." />
      <div className="hah-card p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F5F7FA] border border-[#E5E7EB] rounded-lg flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-[#94A3B8]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search alerts…"
            className="bg-transparent outline-none text-sm w-full" data-testid="rpm-alerts-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44" data-testid="rpm-alerts-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__open__">Open only</SelectItem>
            <SelectItem value="__all__">All statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="acknowledged">Acknowledged</SelectItem>
            <SelectItem value="escalated">Escalated</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="dismissed">Dismissed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger className="w-40"><Filter className="w-3.5 h-3.5 mr-1.5" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All severity</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="warning">Warning</SelectItem>
            <SelectItem value="info">Info</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="hah-card p-8 text-center text-sm text-[#6B7280]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="hah-card p-10 text-center text-sm text-[#6B7280]">
          <BellRing className="w-8 h-8 mx-auto text-[#94A3B8] mb-2" />
          No alerts match the current filters.
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((a) => (
            <div key={a.id} className="hah-card p-4" data-testid={`alert-row-${a.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider ${
                      a.severity === "critical" ? "bg-[#FEE2E2] text-[#991B1B]" :
                      a.severity === "warning" ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#E0F2FE] text-[#075985]"
                    }`}>{a.severity}</span>
                    <span className="text-[10px] uppercase font-semibold text-[#0B7E8C]">{a.status}</span>
                    <span className="text-[10px] uppercase font-semibold text-[#6B7280]">{a.source}</span>
                  </div>
                  <div className="text-sm font-semibold text-[#1F2937]">{a.title}</div>
                  <div className="text-xs text-[#6B7280] mt-0.5">
                    {patientsMap[a.patient_id] || a.patient_id} · {timeAgo(a.created_at)}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" className="h-7 border-[#E5E7EB]"
                    onClick={() => navigate(`/rpm/patients/${a.patient_id}`)}>Open patient</Button>
                  {a.status === "new" && (
                    <Button size="sm" variant="outline" className="h-7 border-[#E5E7EB]"
                      onClick={() => setAction({ id: a.id, action: "acknowledge", notes: "" })}>
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Ack
                    </Button>
                  )}
                  {["new", "acknowledged", "in_progress"].includes(a.status) && (
                    <Button size="sm" variant="outline" className="h-7 border-[#E5E7EB]"
                      onClick={() => setAction({ id: a.id, action: "escalate", reason: "" })}>
                      <ArrowRightCircle className="w-3 h-3 mr-1" /> Escalate
                    </Button>
                  )}
                  {["new", "acknowledged", "in_progress", "escalated"].includes(a.status) && (
                    <Button size="sm" variant="outline" className="h-7 border-[#E5E7EB]"
                      onClick={() => setAction({ id: a.id, action: "resolve", notes: "" })}>
                      <ClipboardPlus className="w-3 h-3 mr-1" /> Resolve
                    </Button>
                  )}
                  {a.status !== "dismissed" && a.status !== "closed" && (
                    <Button size="sm" variant="outline" className="h-7 border-[#EF444433] text-[#EF4444]"
                      onClick={() => setAction({ id: a.id, action: "dismiss", reason: "" })}>
                      <Ban className="w-3 h-3 mr-1" /> Dismiss
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!action} onOpenChange={(o) => { if (!o) setAction(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="capitalize">{action?.action} Alert</DialogTitle></DialogHeader>
          <div className="py-2 space-y-3">
            {(action?.action === "escalate" || action?.action === "dismiss") && (
              <FormField label="Reason" required full>
                <Textarea rows={3} value={action?.reason || ""} onChange={(e) => setAction({ ...action, reason: e.target.value })} />
              </FormField>
            )}
            {(action?.action === "acknowledge" || action?.action === "resolve") && (
              <FormField label={action?.action === "resolve" ? "Intervention notes (required)" : "Notes"} full>
                <Textarea rows={3} value={action?.notes || ""} onChange={(e) => setAction({ ...action, notes: e.target.value })} />
              </FormField>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAction(null)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={doAction} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
